import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import Field from "../../components/admin/Field";
import {
  adminApprovePrep,
  adminCreatePrep,
  adminDeletePrep,
  adminGetPrep,
  adminUpdatePrep,
  listPrepRecipes,
  PREP_CATEGORIES,
  type LinkedCocktail,
  type PrepCategory,
  type PrepDetailResponse,
  type PrepListItem,
  type PrepVersion,
  type UpdatePrepPayload,
} from "../../lib/api";
import type { SessionUser } from "../../types/auth";
import ApproveModal from "../../components/admin/ApproveModal";
import { useIsMobile } from "../../lib/useIsMobile";
import RecipeStudioTabs from "../../components/admin/RecipeStudioTabs";

/**
 * Admin Prep Recipes — list + per-prep editor.
 *
 * URL shape:
 *   /admin/prep-recipes                — list (left), no detail (right)
 *   /admin/prep-recipes/:slug          — list (left), detail+editor (right) on the active version
 *
 * Editor is a single column with: header (name + category + status + linked
 * cocktails count), version selector, fields, save / submit / approve.
 *
 * Auth: bar_manager+ (gated by AdminLayout). bar_lead can edit drafts they
 * created — server enforces.
 */

const FILTERS: { value: "all" | "active_only" | "draft" | "approved" | "hidden" | "retired"; label: string }[] = [
  { value: "all", label: "All" },
  { value: "active_only", label: "Live" },
  { value: "draft", label: "Drafts" },
  { value: "approved", label: "Approved" },
  { value: "hidden", label: "Hidden" },
  { value: "retired", label: "Retired" },
];

export default function AdminPrepRecipes() {
  const navigate = useNavigate();
  const { slug: slugFromUrl } = useParams<{ slug?: string }>();
  const { user } = useOutletContext<{ user: SessionUser }>();
  const isOwner = user.bar_roles.includes("bar_owner");
  const isMobile = useIsMobile();

  const [filter, setFilter] = useState<"all" | "active_only" | "draft" | "approved" | "hidden" | "retired">("all");
  const [groupByCategory, setGroupByCategory] = useState(true);
  const [preps, setPreps] = useState<PrepListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [newPrepBusy, setNewPrepBusy] = useState(false);

  const [detail, setDetail] = useState<PrepDetailResponse | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  // Track + clear the flash timer on unmount and on retrigger so a new
  // flash doesn't get wiped early by a stale older timer.
  const flashTimerRef = useRef<number | null>(null);
  useEffect(() => () => {
    if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
  }, []);

  function showFlash(msg: string) {
    setFlash(msg);
    if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
    flashTimerRef.current = window.setTimeout(() => setFlash(null), 3000);
  }

  /**
   * Group preps by category for the list panel. Returns an array of
   * `[category, items]` pairs in canonical PREP_CATEGORIES order. Empty
   * categories are dropped. Items within each category sort alphabetically.
   */
  const groupedPreps = useMemo(() => {
    const groups = new Map<PrepCategory, PrepListItem[]>();
    for (const cat of PREP_CATEGORIES) groups.set(cat, []);
    for (const p of preps) {
      const cat = (PREP_CATEGORIES.includes(p.category) ? p.category : "other") as PrepCategory;
      const list = groups.get(cat);
      if (list) list.push(p);
    }
    for (const list of groups.values()) {
      list.sort((a, b) => a.name.localeCompare(b.name));
    }
    return Array.from(groups.entries()).filter(([, list]) => list.length > 0);
  }, [preps]);

  /**
   * Flat alphabetical view, used when groupByCategory is off.
   */
  const flatPreps = useMemo(
    () => [...preps].sort((a, b) => a.name.localeCompare(b.name)),
    [preps],
  );

  async function reloadList() {
    setLoading(true);
    setError(null);
    try {
      const r = await listPrepRecipes(filter);
      setPreps(r.preps);
    } catch (err: any) {
      setError(err?.message ?? "Could not load preps");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    reloadList();
  }, [filter]);

  async function reloadDetail() {
    if (!slugFromUrl) {
      setDetail(null);
      return;
    }
    setLoadingDetail(true);
    try {
      const r = await adminGetPrep({ slug: slugFromUrl });
      setDetail(r);
    } catch (err: any) {
      setError(err?.message ?? "Could not load prep");
      setDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  }
  useEffect(() => {
    reloadDetail();
  }, [slugFromUrl]);

  /**
   * Create a stub prep + navigate to its editor in one step. No popup —
   * defaults to "New prep recipe" / "syrup" so the editor renders
   * immediately. Manager renames + categorizes inline; autosave handles
   * the rest. Server auto-slugifies "new-prep-recipe", and the autosave
   * loop refreshes the list (draft chip count) silently.
   */
  async function onDeletePrepConfirm(prep_id: string, slug: string) {
    setDeletingId(prep_id);
    setError(null);
    try {
      await adminDeletePrep(prep_id);
      setPreps((prev) => prev.filter((x) => x.id !== prep_id));
      setConfirmDeleteId(null);
      setDeletingId(null);
      if (slugFromUrl === slug) navigate("/admin/prep-recipes");
    } catch (err: any) {
      setError(err?.message ?? "Could not delete prep");
      setDeletingId(null);
    }
  }

  async function onNewPrep() {
    if (newPrepBusy) return; // belt-and-suspenders against double-tap
    setError(null);
    setNewPrepBusy(true);
    try {
      const r = await adminCreatePrep("New prep recipe", "syrup");
      showFlash("New prep started — fill in the form, autosave is on.");
      navigate(`/admin/prep-recipes/${r.slug}`);
      reloadList();
    } catch (err: any) {
      setError(err?.message ?? "Could not create prep");
    } finally {
      setNewPrepBusy(false);
    }
  }

  return (
    <section style={{ ...styles.shell, padding: isMobile ? "8px 16px 32px" : "8px 0 32px" }}>
      <RecipeStudioTabs activeTab="preps" badgeCounts={{ preps: preps.length }} />
      <div style={styles.headerRow}>
        <div>
          <p style={styles.eyebrow}>Service workflow</p>
          <h1 style={styles.title}>Prep recipes</h1>
        </div>
        <button type="button" onClick={onNewPrep} disabled={newPrepBusy} style={styles.primaryBtn}>
          {newPrepBusy ? "Creating…" : "+ New prep"}
        </button>
      </div>

      {flash && <div style={styles.flash} role="status">{flash}</div>}
      {error && <div style={styles.errorBox} role="alert">{error}</div>}

      <div style={{ ...styles.twoCol, gridTemplateColumns: isMobile ? "1fr" : "minmax(260px, 320px) minmax(0, 1fr)" }}>
        {(!isMobile || !slugFromUrl) && <aside style={styles.listPanel}>
          {/* Toggle-button group with aria-pressed (not a tablist). */}
          <div style={styles.filterRow} role="group" aria-label="Prep status filter">
            {FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                aria-pressed={filter === f.value}
                onClick={() => setFilter(f.value)}
                style={filter === f.value ? styles.pillActive : styles.pill}
              >
                {f.label}
              </button>
            ))}
          </div>
          {/* Sort/group toggle — by category vs flat A-Z. Group-by-category
              is on by default so syrups, batches, etc. stack together. */}
          <div style={styles.sortRow}>
            <span style={styles.sortLabel}>Sort:</span>
            <button
              type="button"
              aria-pressed={groupByCategory}
              onClick={() => setGroupByCategory(true)}
              style={groupByCategory ? styles.sortActive : styles.sortBtn}
            >
              By category
            </button>
            <button
              type="button"
              aria-pressed={!groupByCategory}
              onClick={() => setGroupByCategory(false)}
              style={!groupByCategory ? styles.sortActive : styles.sortBtn}
            >
              A–Z
            </button>
          </div>

          {loading && <p style={styles.muted}>Loading…</p>}
          {!loading && preps.length === 0 && <p style={styles.muted}>No preps match.</p>}

          {!loading && groupByCategory && groupedPreps.map(([cat, items]) => (
            <div key={cat} style={styles.categoryGroup}>
              <h3 style={styles.categoryHeading}>
                {cat}
                <span style={styles.categoryCount}>{items.length}</span>
              </h3>
              <ul style={styles.list}>
                {items.map((p) => renderPrepRow(p, slugFromUrl, navigate, {
                  isOwner,
                  confirmDeleteId,
                  deletingId,
                  onDeleteClick: (id) => setConfirmDeleteId(id),
                  onDeleteConfirm: onDeletePrepConfirm,
                  onDeleteCancel: () => setConfirmDeleteId(null),
                }))}
              </ul>
            </div>
          ))}

          {!loading && !groupByCategory && (
            <ul style={styles.list}>
              {flatPreps.map((p) => renderPrepRow(p, slugFromUrl, navigate, {
                isOwner,
                confirmDeleteId,
                deletingId,
                onDeleteClick: (id) => setConfirmDeleteId(id),
                onDeleteConfirm: onDeletePrepConfirm,
                onDeleteCancel: () => setConfirmDeleteId(null),
              }))}
            </ul>
          )}
        </aside>}

        {(!isMobile || !!slugFromUrl) && <main style={styles.detailPanel}>
          {isMobile && slugFromUrl && (
            <button type="button" onClick={() => navigate("/admin/prep-recipes")} style={styles.backBtn}>
              ← Back to list
            </button>
          )}
          {!slugFromUrl && <p style={styles.muted}>Select a prep on the left to view + edit.</p>}
          {slugFromUrl && loadingDetail && <p style={styles.muted}>Loading prep…</p>}
          {slugFromUrl && !loadingDetail && detail && (
            <PrepEditor
              key={detail.version?.id ?? "no-version"}
              detail={detail}
              isOwner={isOwner}
              onSaved={(msg) => {
                showFlash(msg);
                reloadDetail();
                reloadList();
              }}
              onAutoSaved={() => {
                // Silent list refresh only — DON'T reload detail (would
                // disrupt the user's focus + cursor position mid-typing).
                reloadList();
              }}
            />
          )}
        </main>}
      </div>
    </section>
  );
}

interface PrepEditorProps {
  detail: PrepDetailResponse;
  isOwner: boolean;
  /** Fired on explicit save + approve flows — parent shows a flash AND
   * reloads the list/detail. */
  onSaved: (msg: string) => void;
  /** Fired after a successful autosave — parent should refresh the list
   * silently (draft-count chips, etc.) but NOT reload the detail (would
   * disrupt focus). */
  onAutoSaved?: () => void;
}

type SaveState = "idle" | "dirty" | "saving" | "saved" | "error";
const AUTOSAVE_DEBOUNCE_MS = 1500;

interface PrepRowDeleteOpts {
  isOwner: boolean;
  confirmDeleteId: string | null;
  deletingId: string | null;
  onDeleteClick: (id: string) => void;
  onDeleteConfirm: (prep_id: string, slug: string) => void;
  onDeleteCancel: () => void;
}

/**
 * Renders a single prep row in the list panel. Extracted so both the
 * grouped-by-category and flat A-Z layouts share the same row markup.
 */
function renderPrepRow(
  p: PrepListItem,
  activeSlug: string | undefined,
  navigate: ReturnType<typeof useNavigate>,
  del: PrepRowDeleteOpts,
) {
  const draftCount = p.version_counts.Draft + p.version_counts.Testing + p.version_counts["Needs Review"];
  const isConfirming = del.confirmDeleteId === p.id;
  const isDeleting = del.deletingId === p.id;
  return (
    <li key={p.id} style={{ position: "relative" }}>
      {isConfirming ? (
        <div style={styles.deleteConfirmRow}>
          <span style={styles.deleteConfirmText}>Delete "{p.name}"?</span>
          <div style={styles.deleteConfirmBtns}>
            <button
              type="button"
              onClick={() => del.onDeleteConfirm(p.id, p.slug)}
              disabled={isDeleting}
              style={styles.deleteConfirmBtn}
            >
              {isDeleting ? "Deleting…" : "Delete"}
            </button>
            <button
              type="button"
              onClick={del.onDeleteCancel}
              disabled={isDeleting}
              style={styles.deleteCancelBtn}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => navigate(`/admin/prep-recipes/${p.slug}`)}
          aria-pressed={p.slug === activeSlug}
          style={{
            ...styles.listRow,
            ...(p.slug === activeSlug ? styles.listRowActive : {}),
            paddingRight: del.isOwner ? 36 : 10,
          }}
        >
          <div style={styles.listName}>{p.name}</div>
          <div style={styles.listMeta}>
            {p.category}
            {p.approved_version && ` · v${p.approved_version.version_number}`}
            {" · "}
            {p.service_status}
            {draftCount > 0 && (
              <span style={styles.draftMarker}>
                {" · "}
                {draftCount} draft{draftCount === 1 ? "" : "s"}
              </span>
            )}
          </div>
        </button>
      )}
      {del.isOwner && !isConfirming && (
        <button
          type="button"
          onClick={() => del.onDeleteClick(p.id)}
          style={styles.deleteXBtn}
          title="Delete this prep"
        >
          ×
        </button>
      )}
    </li>
  );
}

/**
 * Small status pill rendered next to the Save button. Tells the manager
 * whether autosave is idle, has unsaved changes, is mid-save, just saved,
 * or errored. Live region (aria-live=polite) so screen readers hear state
 * transitions without being noisy.
 */
function SaveStatusBadge({ state }: { state: SaveState }) {
  const map: Record<SaveState, { label: string; color: string; bg: string; border: string }> = {
    idle:    { label: "—",                  color: "var(--muted)", bg: "transparent",            border: "var(--border)" },
    dirty:   { label: "Unsaved changes",    color: "#FFD789",      bg: "rgba(249,175,54,0.10)",  border: "rgba(249,175,54,0.4)" },
    saving:  { label: "Saving…",            color: "var(--text)",  bg: "var(--bg)",              border: "var(--border)" },
    saved:   { label: "✓ Saved",            color: "#86efac",      bg: "rgba(39,174,96,0.10)",   border: "rgba(39,174,96,0.4)" },
    error:   { label: "✕ Save failed",      color: "#FF8A7A",      bg: "rgba(224,80,64,0.10)",   border: "rgba(224,80,64,0.4)" },
  };
  const v = map[state];
  if (state === "idle") return null; // Hide pill entirely when nothing's happening.
  return (
    <span
      role="status"
      aria-live="polite"
      style={{
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: 0.4,
        textTransform: "uppercase",
        color: v.color,
        background: v.bg,
        border: `1px solid ${v.border}`,
        borderRadius: 999,
        padding: "4px 10px",
        whiteSpace: "nowrap",
      }}
    >
      {v.label}
    </span>
  );
}

function PrepEditor({ detail, isOwner, onSaved, onAutoSaved }: PrepEditorProps) {
  const { prep, version, versions, linked_cocktails } = detail;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [approveOpen, setApproveOpen] = useState(false);

  // Autosave plumbing — debounced 1.5s after the user stops typing. State
  // machine: idle → dirty (timer running) → saving → saved/error → idle.
  // Manual Save & Approve still work; they just bypass the debounce.
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const saveTimerRef = useRef<number | null>(null);
  const dirtyRef = useRef(false);
  const inFlightRef = useRef(false);
  const isInitialRenderRef = useRef(true);
  const skipNextAutosaveRef = useRef(false);

  // Editable fields (only if version is editable).
  const [versionId, setVersionId] = useState(version?.id ?? "");
  const [versionNumber, setVersionNumber] = useState(version?.version_number ?? "");
  const [status, setStatus] = useState<PrepVersion["status"]>(version?.status ?? "Draft");
  // Derive editable from `status` state, not the `version` prop — after
  // onSelectVersion() swaps the displayed version, `version` still points to
  // the originally loaded one.  `status` state is kept in sync by onSelectVersion.
  const editable = !["Approved", "Retired", "Archived"].includes(status);
  const [yieldAmount, setYieldAmount] = useState(
    version?.yield_amount != null ? String(version.yield_amount) : "",
  );
  const [yieldUnit, setYieldUnit] = useState(version?.yield_unit ?? "");
  const [shelfLifeDays, setShelfLifeDays] = useState(
    version?.shelf_life_days != null ? String(version.shelf_life_days) : "",
  );
  const [storage, setStorage] = useState(version?.storage ?? "");
  const [labelingInstructions, setLabelingInstructions] = useState(version?.labeling_instructions ?? "");
  const [ingredientsText, setIngredientsText] = useState(version?.ingredients_text ?? "");
  const [buildInstructions, setBuildInstructions] = useState(version?.build_instructions ?? "");
  const [staffNotes, setStaffNotes] = useState(version?.staff_notes ?? "");
  const [adminNotes, setAdminNotes] = useState(version?.internal_admin_notes ?? "");
  const [warning, setWarning] = useState(version?.warning ?? "");

  const [name, setName] = useState(prep.name);
  const [category, setCategory] = useState<PrepCategory>(prep.category);
  const [serviceStatus, setServiceStatus] = useState(prep.service_status);

  // When the user picks a different version from the dropdown, swap the
  // displayed version (read-only for non-editable ones).
  useEffect(() => {
    setVersionId(version?.id ?? "");
  }, [version?.id]);

  async function onSelectVersion(vid: string) {
    if (vid === versionId) return;
    setBusy(true);
    // The setState calls below are programmatic, not user input — flag the
    // autosave effect so it doesn't immediately try to save the new version's
    // data over itself. (The actual change happens across multiple setState
    // calls; the effect runs once after them all batch.)
    skipNextAutosaveRef.current = true;
    try {
      const r = await adminGetPrep({ prep_id: prep.id, version_id: vid });
      // Hot-swap: rebuild local fields from the new focused version.
      const v = r.version;
      if (!v) return;
      setVersionId(v.id);
      setVersionNumber(v.version_number);
      setStatus(v.status);
      setYieldAmount(v.yield_amount != null ? String(v.yield_amount) : "");
      setYieldUnit(v.yield_unit ?? "");
      setShelfLifeDays(v.shelf_life_days != null ? String(v.shelf_life_days) : "");
      setStorage(v.storage ?? "");
      setLabelingInstructions(v.labeling_instructions ?? "");
      setIngredientsText(v.ingredients_text ?? "");
      setBuildInstructions(v.build_instructions ?? "");
      setStaffNotes(v.staff_notes ?? "");
      setAdminNotes(v.internal_admin_notes ?? "");
      setWarning(v.warning ?? "");
    } finally {
      setBusy(false);
    }
  }

  /**
   * Build the update payload from current form state. Pure — does not
   * mutate React state. Reused by both manual save and autosave.
   */
  function buildPayload(): UpdatePrepPayload | null {
    if (!editable || !versionId) return null;
    const payload: UpdatePrepPayload = {
      version_id: versionId,
      prep: {
        name: name.trim() || prep.name, // never push empty name to server
        category,
        service_status: serviceStatus,
      },
      version: {
        version_number: versionNumber.trim() || version?.version_number || "1",
        yield_amount: yieldAmount.trim() ? Number(yieldAmount) : null,
        yield_unit: yieldUnit.trim() || null,
        shelf_life_days: shelfLifeDays.trim() ? Number(shelfLifeDays) : null,
        storage: storage.trim() || null,
        labeling_instructions: labelingInstructions.trim() || null,
        ingredients_text: ingredientsText.trim() || null,
        build_instructions: buildInstructions.trim() || null,
        staff_notes: staffNotes.trim() || null,
        internal_admin_notes: adminNotes.trim() || null,
        warning: warning.trim() || null,
      },
    };
    const editableStatuses = ["Draft", "Testing", "Needs Review"] as const;
    if (status && (editableStatuses as readonly string[]).includes(status)) {
      payload.version!.status = status as "Draft" | "Testing" | "Needs Review";
    }
    return payload;
  }

  /**
   * Manual save (still bound to the "Save now" button + Cmd+S in future).
   * Triggers the parent's `onSaved` which shows a flash + reloads. For
   * autosave we go through doAutoSave() instead.
   */
  async function save() {
    const payload = buildPayload();
    if (!payload) return;
    setBusy(true);
    setError(null);
    try {
      await adminUpdatePrep(payload);
      // Manual save resets the autosave state machine — we just persisted.
      dirtyRef.current = false;
      setSaveState("saved");
      window.setTimeout(() => {
        setSaveState((s) => (s === "saved" ? "idle" : s));
      }, 2000);
      onSaved("Saved.");
    } catch (err: any) {
      setError(err?.message ?? "Save failed");
      setSaveState("error");
    } finally {
      setBusy(false);
    }
  }

  /**
   * Autosave — fired by the debounced effect below. Serializes against
   * itself with `inFlightRef`. If new edits arrive while a save is in
   * flight, the post-flight check kicks off another save without waiting
   * for the user to stop typing again.
   */
  async function doAutoSave() {
    if (inFlightRef.current) return;
    const payload = buildPayload();
    if (!payload) return;
    inFlightRef.current = true;
    dirtyRef.current = false;
    setSaveState("saving");
    setError(null);
    try {
      await adminUpdatePrep(payload);
      if (dirtyRef.current) {
        // More edits came in mid-save — schedule another after the same debounce.
        setSaveState("dirty");
        if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
        saveTimerRef.current = window.setTimeout(() => void doAutoSave(), AUTOSAVE_DEBOUNCE_MS);
      } else {
        setSaveState("saved");
        // Fade the "Saved" indicator back to idle after a short beat.
        window.setTimeout(() => {
          setSaveState((s) => (s === "saved" ? "idle" : s));
        }, 2000);
        // Refresh the list silently so chips like "1 draft" update.
        onAutoSaved?.();
      }
    } catch (err: any) {
      setSaveState("error");
      setError(err?.message ?? "Autosave failed");
    } finally {
      inFlightRef.current = false;
    }
  }

  // Watch every editable field. On any change after the initial render,
  // mark dirty + (re)start the debounce. `skipNextAutosaveRef` blocks the
  // first run after `onSelectVersion` (programmatic state update).
  useEffect(() => {
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false;
      return;
    }
    if (skipNextAutosaveRef.current) {
      skipNextAutosaveRef.current = false;
      return;
    }
    if (!editable || !version) return;

    dirtyRef.current = true;
    setSaveState("dirty");
    if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(() => void doAutoSave(), AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (saveTimerRef.current) window.clearTimeout(saveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    name, category, serviceStatus,
    versionNumber, status,
    yieldAmount, yieldUnit, shelfLifeDays, storage,
    labelingInstructions, ingredientsText, buildInstructions,
    staffNotes, adminNotes, warning,
    editable, version?.id,
  ]);

  async function onApprove(summary: string) {
    if (!versionId) throw new Error("No version");
    await adminApprovePrep(versionId, summary);
    setApproveOpen(false);
    onSaved("Approved.");
  }

  return (
    <div style={styles.editorShell}>
      <header style={styles.editorHeader}>
        <div>
          <p style={styles.eyebrow}>{category}</p>
          <h2 style={styles.editorTitle}>{name}</h2>
          <div style={styles.headerChips}>
            <Chip label={`v${versionNumber}`} tone="muted" />
            <Chip label={status} tone={status === "Approved" ? "approved" : "muted"} />
            <Chip label={prep.service_status} tone={prep.service_status === "Active" ? "approved" : "muted"} />
            {linked_cocktails.length > 0 && (
              <Chip
                label={`${linked_cocktails.length} cocktail${linked_cocktails.length === 1 ? "" : "s"}`}
                tone="gold"
              />
            )}
          </div>
        </div>
        <div style={styles.headerActions}>
          {versions.length > 1 && (
            <select
              value={versionId}
              onChange={(e) => onSelectVersion(e.target.value)}
              style={styles.versionSelect}
              disabled={busy}
              aria-label="Switch prep version"
            >
              {versions.map((v) => (
                <option key={v.id} value={v.id}>
                  v{v.version_number} · {v.status}
                </option>
              ))}
            </select>
          )}
          {editable && (
            <SaveStatusBadge state={saveState} />
          )}
          {editable && (
            <button type="button" onClick={save} disabled={busy} style={styles.saveBtn}>
              {busy ? "Saving…" : "Save now"}
            </button>
          )}
          {editable && isOwner && (
            <button
              type="button"
              onClick={() => setApproveOpen(true)}
              disabled={busy}
              style={styles.approveBtn}
              title="Promote this draft to Approved (retires the previous Approved version)"
            >
              Approve & Publish
            </button>
          )}
        </div>
      </header>

      {linked_cocktails.length > 0 && (
        <LinkedCocktailsBlock linked={linked_cocktails} />
      )}

      {error && <div style={styles.errorBox} role="alert">{error}</div>}

      {!editable && (
        <p style={styles.lockedNote}>
          This version is <strong>{status}</strong> and cannot be edited. Switch to a Draft (or create a new one) to make changes.
        </p>
      )}

      <Field label="Name">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={styles.input}
          disabled={!editable || busy}
          // Auto-focus when this is a freshly-stubbed prep ("+ New prep"
          // creates with default name "New prep recipe"). The manager
          // renames immediately; autosave saves it on debounce.
          autoFocus={prep.name === "New prep recipe"}
          onFocus={(e) => {
            // Select-all on first focus of a fresh stub so typing replaces
            // the placeholder name in one keystroke.
            if (prep.name === "New prep recipe") e.currentTarget.select();
          }}
        />
      </Field>

      <FieldRow>
        <Field label="Category">
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as PrepCategory)}
            style={styles.input}
            disabled={!editable || busy}
          >
            {PREP_CATEGORIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </Field>
        <Field label="Service status">
          <select
            value={serviceStatus}
            onChange={(e) => setServiceStatus(e.target.value as any)}
            style={styles.input}
            disabled={!editable || busy}
          >
            <option value="Active">Active</option>
            <option value="Hidden">Hidden</option>
            <option value="Retired">Retired</option>
          </select>
        </Field>
      </FieldRow>

      <FieldRow>
        <Field label="Version number">
          <input
            value={versionNumber}
            onChange={(e) => setVersionNumber(e.target.value)}
            placeholder="e.g. 0.1, 1.4, 2.0"
            style={styles.input}
            disabled={!editable || busy}
          />
        </Field>
        <Field label="Status">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as any)}
            style={styles.input}
            disabled={!editable || busy}
          >
            <option value="Draft">Draft</option>
            <option value="Testing">Testing</option>
            <option value="Needs Review">Needs Review</option>
          </select>
        </Field>
      </FieldRow>

      <FieldRow>
        <Field label="Yield">
          <input
            type="number"
            inputMode="decimal"
            step="any"
            value={yieldAmount}
            onChange={(e) => setYieldAmount(e.target.value)}
            placeholder="e.g. 750"
            style={styles.input}
            disabled={!editable || busy}
          />
        </Field>
        <Field label="Yield unit">
          <input
            value={yieldUnit}
            onChange={(e) => setYieldUnit(e.target.value)}
            placeholder="ml · oz · L"
            style={styles.input}
            disabled={!editable || busy}
          />
        </Field>
      </FieldRow>

      <FieldRow>
        <Field label="Shelf life (days)">
          <input
            type="number"
            inputMode="numeric"
            value={shelfLifeDays}
            onChange={(e) => setShelfLifeDays(e.target.value)}
            placeholder="e.g. 14"
            style={styles.input}
            disabled={!editable || busy}
          />
        </Field>
        <Field label="Storage">
          <input
            value={storage}
            onChange={(e) => setStorage(e.target.value)}
            placeholder="Refrigerated · Room temp · Frozen"
            style={styles.input}
            disabled={!editable || busy}
          />
        </Field>
      </FieldRow>

      <Field label="Labeling instructions">
        <textarea
          value={labelingInstructions}
          onChange={(e) => setLabelingInstructions(e.target.value)}
          rows={2}
          placeholder='e.g. "Label with date + initials"'
          style={styles.textarea}
          disabled={!editable || busy}
        />
      </Field>

      <Field label="Ingredients">
        <textarea
          value={ingredientsText}
          onChange={(e) => setIngredientsText(e.target.value)}
          rows={5}
          placeholder='One per line — e.g. "200g sugar"'
          style={styles.textarea}
          disabled={!editable || busy}
        />
      </Field>

      <Field label="Build instructions">
        <textarea
          value={buildInstructions}
          onChange={(e) => setBuildInstructions(e.target.value)}
          rows={6}
          placeholder="Step-by-step build."
          style={styles.textarea}
          disabled={!editable || busy}
        />
      </Field>

      <Field label="Staff notes (visible to bartenders)">
        <textarea
          value={staffNotes}
          onChange={(e) => setStaffNotes(e.target.value)}
          rows={2}
          style={styles.textarea}
          disabled={!editable || busy}
        />
      </Field>

      <Field label="Internal admin notes (admin-only)">
        <textarea
          value={adminNotes}
          onChange={(e) => setAdminNotes(e.target.value)}
          rows={2}
          style={styles.textarea}
          disabled={!editable || busy}
        />
      </Field>

      <Field label="Warning">
        <input
          value={warning}
          onChange={(e) => setWarning(e.target.value)}
          placeholder='e.g. "Discontinued — use Demerara 2:1 instead"'
          style={styles.input}
          disabled={!editable || busy}
        />
      </Field>

      {approveOpen && (
        <ApproveModal
          open={approveOpen}
          versionNumber={versionNumber}
          drinkName={name}
          hasPreviousApproved={!!prep.active_approved_version_id && prep.active_approved_version_id !== version?.id}
          onClose={() => setApproveOpen(false)}
          onApprove={onApprove}
        />
      )}
    </div>
  );
}

function LinkedCocktailsBlock({ linked }: { linked: LinkedCocktail[] }) {
  return (
    <div style={styles.linkedBlock} role="region" aria-labelledby="linked-cocktails-heading">
      <p id="linked-cocktails-heading" style={styles.linkedHeading}>
        Used in {linked.length} cocktail{linked.length === 1 ? "" : "s"} — changes here change all of them.
      </p>
      <ul style={styles.linkedList}>
        {linked.map((l) => (
          <li key={l.version_id} style={styles.linkedItem}>
            <strong>{l.drink_name}</strong>
            <span style={styles.subMeta}>
              {" "}· v{l.version_number} · {l.service_status} · via {l.link_via === "ingredient" ? "ingredient" : "finish step"}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Field is imported from ../../components/admin/Field (shared a11y-correct version).
function FieldRow({ children }: { children: React.ReactNode }) {
  return <div style={styles.fieldRow}>{children}</div>;
}
function Chip({ label, tone }: { label: string; tone: "muted" | "approved" | "gold" | "blue" }) {
  return <span style={{ ...styles.chip, ...chipTone[tone] }}>{label}</span>;
}

const chipTone: Record<string, CSSProperties> = {
  approved: { background: "rgba(39,174,96,0.16)", color: "#86efac", borderColor: "rgba(39,174,96,0.3)" },
  gold: { background: "rgba(249,175,54,0.16)", color: "var(--gold)", borderColor: "rgba(249,175,54,0.3)" },
  blue: { background: "rgba(96,165,250,0.16)", color: "#93c5fd", borderColor: "rgba(96,165,250,0.3)" },
  muted: { background: "var(--bg)", color: "var(--muted)", borderColor: "var(--border)" },
};

const styles: Record<string, CSSProperties> = {
  shell: { maxWidth: 1200, padding: "8px 0 32px" },
  backBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "transparent",
    color: "var(--muted)",
    border: "none",
    padding: "0 0 12px",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    gap: 12,
    marginBottom: 12,
    flexWrap: "wrap",
  },
  eyebrow: {
    margin: 0,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "var(--gold)",
  },
  title: {
    fontFamily: "Georgia, serif",
    fontSize: 28,
    margin: "8px 0 0",
    letterSpacing: -0.4,
    color: "var(--text)",
  },
  primaryBtn: {
    background: "var(--gold)",
    color: "#0D0B08",
    border: 0,
    borderRadius: 10,
    padding: "10px 16px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    minHeight: 40,
  },
  flash: {
    margin: "0 0 12px",
    padding: 10,
    background: "rgba(39,174,96,0.12)",
    border: "1px solid rgba(39,174,96,0.4)",
    color: "#86efac",
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 700,
  },
  errorBox: {
    margin: "0 0 12px",
    padding: 10,
    background: "rgba(224,80,64,0.1)",
    border: "1px solid rgba(224,80,64,0.4)",
    color: "#FF8A7A",
    borderRadius: 10,
    fontSize: 12,
  },
  twoCol: {
    display: "grid",
    gridTemplateColumns: "minmax(260px, 320px) minmax(0, 1fr)",
    gap: 16,
    alignItems: "flex-start",
  },
  listPanel: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 14,
    padding: 12,
  },
  filterRow: { display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 12 },
  // Sort/group toggle (By category vs A-Z) — sits below the filter pills.
  sortRow: {
    display: "flex",
    gap: 6,
    alignItems: "center",
    marginBottom: 8,
    flexWrap: "wrap",
  },
  sortLabel: {
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: "var(--muted)",
  },
  sortBtn: {
    background: "transparent",
    color: "var(--muted)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    padding: "4px 10px",
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
  },
  sortActive: {
    background: "var(--surface)",
    color: "var(--text)",
    border: "1px solid var(--gold)",
    borderRadius: 8,
    padding: "4px 10px",
    fontSize: 11,
    fontWeight: 800,
    cursor: "pointer",
  },
  // Category group — heading + nested list. Renders inside the listPanel
  // when groupByCategory is on.
  categoryGroup: {
    marginBottom: 16,
  },
  categoryHeading: {
    fontFamily: "Georgia, serif",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "var(--gold)",
    margin: "0 0 6px",
    paddingBottom: 4,
    borderBottom: "1px solid var(--border)",
    display: "flex",
    alignItems: "baseline",
    gap: 6,
  },
  categoryCount: {
    fontSize: 10,
    fontWeight: 700,
    color: "var(--muted)",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    letterSpacing: 0,
    textTransform: "none",
  },
  pill: {
    background: "var(--bg)",
    color: "var(--muted)",
    border: "1px solid var(--border)",
    borderRadius: 999,
    padding: "6px 12px",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    cursor: "pointer",
  },
  pillActive: {
    background: "var(--gold)",
    color: "#0D0B08",
    border: "1px solid var(--gold)",
    borderRadius: 999,
    padding: "6px 12px",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    cursor: "pointer",
  },
  list: { listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 4 },
  deleteXBtn: {
    position: "absolute",
    top: "50%",
    right: 6,
    transform: "translateY(-50%)",
    background: "transparent",
    color: "var(--muted)",
    border: "none",
    borderRadius: 6,
    width: 26,
    height: 26,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
    cursor: "pointer",
    padding: 0,
    lineHeight: 1,
  },
  deleteConfirmRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    padding: "8px 10px",
    background: "rgba(224,80,64,0.06)",
    border: "1px solid rgba(224,80,64,0.3)",
    borderRadius: 10,
  },
  deleteConfirmText: {
    flex: 1,
    fontSize: 12,
    color: "var(--text)",
    fontWeight: 600,
    minWidth: 0,
  },
  deleteConfirmBtns: { display: "flex", gap: 6, alignItems: "center" },
  deleteConfirmBtn: {
    background: "#E05040",
    color: "#fff",
    border: 0,
    borderRadius: 8,
    padding: "6px 12px",
    fontSize: 11,
    fontWeight: 800,
    cursor: "pointer",
    minHeight: 30,
  },
  deleteCancelBtn: {
    background: "transparent",
    color: "var(--muted)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    padding: "6px 12px",
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
    minHeight: 30,
  },
  listRow: {
    width: "100%",
    textAlign: "left",
    background: "transparent",
    color: "var(--text)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: 10,
    cursor: "pointer",
  },
  listRowActive: {
    background: "var(--bg)",
    borderColor: "var(--gold)",
  },
  listName: { fontSize: 13, fontWeight: 800 },
  listMeta: { fontSize: 11, color: "var(--muted)", marginTop: 2 },
  draftMarker: { color: "var(--gold)", fontWeight: 700 },
  detailPanel: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 14,
    padding: 16,
  },
  muted: { color: "var(--muted)", fontSize: 13, padding: 8 },
  editorShell: { display: "grid", gap: 10 },
  editorHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 8,
  },
  editorTitle: {
    fontFamily: "Georgia, serif",
    fontSize: 22,
    margin: "4px 0 6px",
    color: "var(--text)",
  },
  headerChips: { display: "flex", gap: 4, flexWrap: "wrap" },
  headerActions: { display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" },
  versionSelect: {
    height: 36,
    background: "var(--bg)",
    color: "var(--text)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: "0 10px",
    fontSize: 12,
    fontWeight: 700,
  },
  saveBtn: {
    background: "var(--gold)",
    color: "#0D0B08",
    border: 0,
    borderRadius: 10,
    padding: "8px 14px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    minHeight: 40,
  },
  approveBtn: {
    background: "transparent",
    color: "var(--gold)",
    border: "1px solid rgba(249,175,54,0.4)",
    borderRadius: 10,
    padding: "8px 14px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    minHeight: 40,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    padding: "3px 10px",
    borderRadius: 999,
    border: "1px solid",
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
  },
  linkedBlock: {
    background: "rgba(249,175,54,0.06)",
    border: "1px solid rgba(249,175,54,0.3)",
    borderRadius: 12,
    padding: 12,
    marginBottom: 8,
  },
  linkedHeading: {
    margin: 0,
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.4,
    color: "var(--gold)",
    textTransform: "uppercase",
  },
  linkedList: { listStyle: "none", padding: 0, margin: "8px 0 0", display: "grid", gap: 4 },
  linkedItem: { fontSize: 13, color: "var(--text)" },
  subMeta: {
    color: "var(--muted)",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 11,
  },
  lockedNote: {
    background: "rgba(96,165,250,0.08)",
    border: "1px solid rgba(96,165,250,0.3)",
    color: "#93c5fd",
    borderRadius: 10,
    padding: 10,
    fontSize: 12,
  },
  field: { display: "grid", gap: 6 },
  fieldRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))",
    gap: 12,
  },
  fieldLabel: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    color: "var(--muted)",
  },
  input: {
    height: 36,
    borderRadius: 10,
    border: "1px solid var(--border)",
    background: "var(--bg)",
    color: "var(--text)",
    padding: "0 10px",
    fontSize: 16,
  },
  textarea: {
    boxSizing: "border-box",
    width: "100%",
    background: "var(--bg)",
    color: "var(--text)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: 10,
    fontSize: 13,
    fontFamily: "inherit",
    resize: "vertical",
  },
};
